<template>
  <modal-wrap :id="modalName" :name="modalName" :hide-footer="true">
    <template #modal-title>
      <h4 class="mb-0">{{ title }}</h4>
    </template>
    <VeeForm @submit="handleSubmit">
      <div class="mt-2">
        <!-- report type selector (create mode only) -->
        <div v-if="!isEdit" class="form-group">
          <Field
            v-slot="{ field, errors, meta }"
            :rules="{ required: true }"
            name="reportId"
            :model-value="form.reportId"
          >
            <label>Report type *</label>
            <select v-bind="field" v-model="form.reportId" :class="['form-select', getValidationState(meta)]">
              <option value="" disabled>Select report</option>
              <option v-for="report in reports" :key="report.id" :value="report.id">{{ report.name }}</option>
            </select>
            <span class="text-danger">{{ errors.join() }}</span>
          </Field>
        </div>
        <!-- recipient hidden for slack channel reports -->
        <div v-if="!isSlackChannel" class="form-group">
          <Field
            v-slot="{ field, errors, meta }"
            :rules="recipientRules"
            name="recipient"
            :model-value="form.recipient"
          >
            <label>Email</label>
            <input
              v-bind="field"
              v-model="form.recipient"
              type="email"
              :class="['form-control', getValidationState(meta)]"
            />
            <span class="text-danger">{{ errors.join() }}</span>
          </Field>
        </div>
        <div class="form-group">
          <Field
            v-slot="{ field, errors, meta }"
            :rules="{ required: true }"
            name="interval"
            :model-value="form.interval"
          >
            <label>Interval *</label>
            <select v-bind="field" v-model="form.interval" :class="['form-select', getValidationState(meta)]">
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
            <span class="text-danger">{{ errors.join() }}</span>
          </Field>
        </div>
        <div class="form-group">
          <Field v-slot="{ field, errors, meta }" :rules="{ required: true }" name="time" :model-value="form.time">
            <label>Time (HH:MM) *</label>
            <input
              v-bind="field"
              v-model="form.time"
              type="time"
              placeholder="09:00"
              :class="['form-control', getValidationState(meta)]"
            />
            <span class="text-danger">{{ errors.join() }}</span>
          </Field>
        </div>
        <div class="mt-3 d-flex">
          <b-button type="submit" variant="success" class="btn-green flex-grow-1">{{ submitText }}</b-button>
          <b-button type="button" class="btn-grey ms-3" @click="close">Cancel</b-button>
        </div>
      </div>
    </VeeForm>
  </modal-wrap>
</template>
<script lang="ts">
import type { ReportSubscriptionModalPayload } from '@/types/Components/Modals'
import type { Report } from '@/types/Models/Report'
import { BButton } from 'bootstrap-vue-next'
import { Form as VeeForm, Field } from 'vee-validate'
import { defineComponent, computed, ref, watch, onMounted, type ComputedRef } from 'vue'
import { REPORT_SUBSCRIPTION_ACTION } from '@/const/ModalTypes'
import { registerModals } from '@/services/Modal'
import { getValidationState } from '@/services/Validation'
import { useReportStore } from '@/store/report'
import { useReportSubscriptionStore } from '@/store/reportSubscription'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

export default defineComponent({
  name: 'ReportSubscriptionActionModal',
  components: { ModalWrap, VeeForm, Field, BButton },
  setup() {
    const modalName = REPORT_SUBSCRIPTION_ACTION
    const modalStore = useVueNextModalStore()
    const subscriptionStore = useReportSubscriptionStore()
    const reportStore = useReportStore()

    const modalData: ComputedRef<ReportSubscriptionModalPayload> = computed(() => modalStore.modalPropsByName(modalName) || {})

    const reports: ComputedRef<Report[]> = computed(() => reportStore.list)

    const form = ref({
      reportId: '' as number | string,
      recipient: '',
      interval: 'day',
      time: '09:00',
    })

    // find current report and determine if channel is slack
    const currentReport = computed(() =>
      reports.value.find((r) => r.id === (isEdit.value ? modalData.value.reportId : Number(form.value.reportId))),
    )
    const isSlackChannel = computed(() => currentReport.value?.channel === 'slack')
    const recipientRules = computed(() => (isSlackChannel.value ? {} : { required: true, email: true }))

    const isEdit = computed(() => Boolean(modalData.value.subscription))
    const title = computed(() => (isEdit.value ? 'Edit Subscription' : 'Add Subscription'))
    const submitText = computed(() => (isEdit.value ? 'Save' : 'Create'))

    const onSuccess = () => {
      reportStore.fetchAll()
      subscriptionStore.fetchAll()
      close()
    }

    // create subscription
    const createSubscription = () => {
      const normalizedTime = form.value.time && form.value.time.length === 5 ? form.value.time + ':00' : form.value.time
      const recipient = isSlackChannel.value ? null : form.value.recipient
      subscriptionStore
        .subscribe(Number(form.value.reportId), recipient, form.value.interval, normalizedTime)
        .then(onSuccess)
    }

    // update subscription
    const updateSubscription = () => {
      if (!modalData.value.subscription) return
      const normalizedTime = form.value.time && form.value.time.length === 5 ? form.value.time + ':00' : form.value.time
      const recipient = isSlackChannel.value ? null : form.value.recipient
      subscriptionStore
        .update(modalData.value.subscription.id, {
          recipient,
          interval: form.value.interval,
          time: normalizedTime,
        })
        .then(onSuccess)
    }

    const handleSubmit = () => (isEdit.value ? updateSubscription() : createSubscription())

    const close = () => modalStore.closeModal(modalName)

    // watch modal data and initialize form
    watch(
      modalData,
      () => {
        form.value.reportId = modalData.value.subscription?.reportId || modalData.value.reportId || ''
        form.value.recipient = isSlackChannel.value
          ? ''
          : modalData.value.subscription?.recipient || modalData.value.recipient || ''
        form.value.interval = modalData.value.subscription?.interval || modalData.value.interval || 'day'
        const rawTime = modalData.value.subscription?.time || modalData.value.time || '09:00'
        form.value.time = rawTime.length >= 5 ? rawTime.slice(0, 5) : rawTime
      },
      { deep: true, immediate: true },
    )

    onMounted(() => registerModals(modalName))

    return {
      form,
      title,
      reports,
      isEdit,
      modalName,
      modalData,
      submitText,
      isSlackChannel,
      recipientRules,
      getValidationState,
      handleSubmit,
      close,
    }
  },
})
</script>
